import { Signal, signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import {
  cloneDeep,
  compact,
  difference,
  filter,
  forEach,
  isEmpty,
  isEqual,
  isNumber,
  isString,
  keys,
  map,
  pick,
  some,
  times,
  uniq,
} from 'es-toolkit/compat';
import { useCallback, useTransition } from 'react';
import { useQueryParam } from 'use-query-params';

import { MAX_VIEWERS } from 'components/Procedure/SlidesViewer/constants';
import {
  getViewerIndexedUrlSetting,
  hasExplicitMultiViewerSettings,
  shouldAllowViewerUrlStateFallback,
  useViewerLayoutUrlState,
} from 'components/Procedure/viewerStateUrlFallback';
import { getExperimentResultFromHeatmapId } from 'components/Procedure/useSlideChannelsAndResults/parseHeatmaps';
import { experimentResultsRequiringPreSigning } from 'components/Procedure/useSlideChannelsAndResults/usePreSignPresentationInfoByExperimentResultIds';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { statelessUpdateUrlParams } from 'utils/statelessUpdateUrlParams';

export interface BaseSlideVisualSettings {
  show: boolean;
  opacity: number;
  gamma?: number;
  contrast?: number;
  brightness?: number;
}

export interface LayerVisualizationSettings {
  show: boolean;
  opacity: number;
  selected?: boolean;
  color?: any;
  colorMap?: string;
  filters?: {
    min?: number;
    max?: number;
    rangeMin?: number;
    rangeMax?: number;
  };
  // Should the lower and upper bounds of the color map be clamped to the filter range (i.e. color the values relative to each other, rather than the min/max of the data)
  clampColorMapToFilterRange?: boolean;
  additionalProperties?: any;
}

export type LayerIdComponentProps =
  // A layer showing a group of layers (i.e. composite raster heatmap)
  // In this case, layerId = parentKey
  | {
      parentKey: string;
      layerName?: string;
    }
  // A layer without a parent key (i.e. single layer)
  // In this case, layerId = layerName
  | {
      parentKey?: string;
      layerName: string;
    }
  // A layer with a parent key and layer name (i.e. grouped layer)
  // In this case, layerId = `${parentKey}-${layerName}`
  | {
      parentKey: string;
      layerName: string;
    };

export const getSettingsKeyFromLayerIdComponents = ({ parentKey, layerName }: LayerIdComponentProps): string => {
  if (parentKey && layerName) {
    return `${parentKey}-${layerName}`;
  }
  if (parentKey) {
    return parentKey;
  }
  if (layerName) {
    return layerName;
  }
  console.warn('Invalid LayerIdComponentProps: either parentKey or layerName must be provided');
  return '';
};

export interface LayerVisualizationChange {
  layerId: string;
  newSettings: Partial<LayerVisualizationSettings>;
  // This is the key for the layer settings in the URL, in case layerId is specific to a slide
  // This allows us to apply settings to the same layer across different slides (i.e. when navigating between slides)
  layerUrlKey?: string;
}

// { [slideId]: BaseSlideVisualSettings }
export const baseSlidesVisualSettings = times(MAX_VIEWERS, () => signal<Record<string, BaseSlideVisualSettings>>({}));

// { [slideId]: [componentId]: Signal<BaseSlideVisualSettings> }
export const deconvolutionComponentsVisualSettings = times(MAX_VIEWERS, () =>
  signal<Record<string, Record<string, Signal<BaseSlideVisualSettings>>>>({})
);

// { [slideId]: { [layerId]: Signal<LayerVisualizationSettings> } }
export const slidesLayerVisualizationSettings = times(MAX_VIEWERS, () =>
  signal<Record<string, Record<string, Signal<LayerVisualizationSettings>>>>({})
);

export const clearVisualizationSettingSignals = () => {
  forEach(baseSlidesVisualSettings, (viewerSettings) => {
    viewerSettings.value = {};
  });
  forEach(deconvolutionComponentsVisualSettings, (viewerSettings) => {
    viewerSettings.value = {};
  });
  forEach(slidesLayerVisualizationSettings, (viewerSettings) => {
    viewerSettings.value = {};
  });
};

export const defaultSlideOpacity = 100;
export const defaultRasterOpacity = 100;
export const defaultPolygonFillOpacity = 30;
export const defaultPolygonStrokeOpacity = 100;
export const defaultPointOpacity = 100;

const NO_GAMMA = 1;
const NO_BRIGHTNESS = 0;
const NO_CONTRAST = 1;

export const defaultBaseSlideVisualSettings: BaseSlideVisualSettings = {
  show: true,
  opacity: defaultSlideOpacity,
  gamma: NO_GAMMA,
  brightness: NO_BRIGHTNESS,
  contrast: NO_CONTRAST,
};
export const defaultDeconvolutionComponentSlideVisualSettings: BaseSlideVisualSettings = {
  show: false,
  opacity: defaultSlideOpacity,
};
export const defaultLayerVisualizationSettings: LayerVisualizationSettings = {
  show: false,
  opacity: 100,
  selected: false,
};

export const isHeatmapActive = ({
  heatmapId,
  viewerIndex,
  slideId,
}: {
  heatmapId: string;
  viewerIndex: number;
  slideId: string;
}) => {
  const viewerSlideLayerVisualizationSettings = slidesLayerVisualizationSettings[viewerIndex];
  const slideLayerVisualizationSettings = viewerSlideLayerVisualizationSettings?.value?.[slideId];
  if (!slideLayerVisualizationSettings) {
    return false;
  }

  const slideHeatmapSettingsKeys = keys(slideLayerVisualizationSettings);
  return some(
    slideHeatmapSettingsKeys,
    (key) =>
      // Check if the heatmap is selected or any of its layers are selected
      (key === heatmapId || key.startsWith(`${heatmapId}-`)) && slideLayerVisualizationSettings[key]?.value?.selected
  );
};

export const getActiveGroupHeatmaps = ({
  heatmapGroupId,
  viewerIndex,
  slideId,
}: {
  heatmapGroupId: string;
  viewerIndex: number;
  slideId: string;
}) => {
  const viewerSlideLayerToggles = slidesLayerVisualizationSettings[viewerIndex];
  const slideLayerToggles = viewerSlideLayerToggles?.value?.[slideId];
  if (!slideLayerToggles) {
    return [];
  }

  const slideHeatmapSettingsKeys = keys(slideLayerToggles);
  return filter(
    slideHeatmapSettingsKeys,
    (key) =>
      // Check if the heatmap is selected or any of its layers are selected
      (key === heatmapGroupId || key.startsWith(`${heatmapGroupId}-`)) && slideLayerToggles[key]?.value?.selected
  );
};

const urlKeyForLayerSettingsPerStainAndDisplayName = 'layerSettingsPerStainAndDisplayName' as const;

// We store heatmap settings by stain and layer key (generally the heatmap's displayName) to allow us to apply settings to the same heatmap across different slides.
// For a given layer, we will prefer settings for a matching stain or viewer, but still defer to any settings for the layer.
export const useUrlLayerSettingsPerStainAndDisplayName = () =>
  useQueryParam<{
    [layerUrlKey: string]: { [stainTypeId: string]: { [viewerIndex: number]: LayerVisualizationSettings } };
  }>(urlKeyForLayerSettingsPerStainAndDisplayName, CompressedJsonParam);

export const useGetLayerSettingsFromUrl = () => {
  const [urlLayerSettingsPerStainAndViewer] = useUrlLayerSettingsPerStainAndDisplayName();
  const { selectedSlideIds, viewersStainPreferences } = useViewerLayoutUrlState();

  return useCallback(
    ({ layerUrlKey, stainTypeId, viewerIndex }: { layerUrlKey: string; stainTypeId: string; viewerIndex: number }) => {
      const layerUrlSettingsDictionary = urlLayerSettingsPerStainAndViewer?.[layerUrlKey];

      const stainTypeIdSettings = layerUrlSettingsDictionary?.[stainTypeId];
      const shouldAllowViewerUrlFallback = shouldAllowViewerUrlStateFallback({
        hasExplicitMultiViewerState: hasExplicitMultiViewerSettings(layerUrlSettingsDictionary),
        selectedSlideIds,
        viewersStainPreferences,
      });

      // Prefer settings for the stain and viewer
      const matchByStainAndViewer = getViewerIndexedUrlSetting({
        settingsByViewer: stainTypeIdSettings,
        viewerIndex,
        shouldAllowFallback: shouldAllowViewerUrlFallback,
      });
      if (matchByStainAndViewer) {
        return matchByStainAndViewer;
      }

      return {} as LayerVisualizationSettings;
    },
    [urlLayerSettingsPerStainAndViewer, selectedSlideIds, viewersStainPreferences]
  );
};

export const useApplyChangesToSlideLayerVisualizationSettings = () => {
  useSignals();

  const [urlLayerSettingsPerStainAndViewer] = useUrlLayerSettingsPerStainAndDisplayName();

  const [, startTransition] = useTransition();

  const applyChangesToSlideLayerVisualizationSettings = ({
    slideId,
    viewerIndex,
    changes,
    skipUrlUpdate,
    stainTypeId,
    debug,
    changeFlow,
  }: {
    slideId: string;
    viewerIndex: number;
    changes: LayerVisualizationChange[];
    skipUrlUpdate?: boolean;
    stainTypeId?: string; // If passed, we will update the URL with the new settings
    debug?: boolean;
    changeFlow: string; // Used for console messages
  }) => {
    if (isEmpty(changes)) {
      if (debug) {
        console.debug(`${changeFlow}: No changes to apply`);
      }
      return;
    }
    if (debug) {
      console.debug(`${changeFlow}: Applying changes to slide layer visualization settings`, {
        slideId,
        viewerIndex,
        changes,
        skipUrlUpdate,
        stainTypeId,
      });
    }
    if (!slidesLayerVisualizationSettings[viewerIndex]?.value) {
      console.error(`${changeFlow}: Invalid viewerIndex: ${viewerIndex}`);
      return false;
    }
    const viewerSettingsSignal = slidesLayerVisualizationSettings[viewerIndex];
    const viewerSettings = viewerSettingsSignal.value;
    const existingSlideSettings = viewerSettings[slideId] || {};
    const missingLayerSettings: Record<string, Signal<LayerVisualizationSettings>> = {};
    const appliedMissingLayerChanges = new Set<LayerVisualizationChange>();
    forEach(changes, (change) => {
      const { layerId, newSettings } = change;
      if (existingSlideSettings[layerId]) {
        return;
      }
      const currentLayerSettings = missingLayerSettings[layerId]?.value || defaultLayerVisualizationSettings;
      if (!isEqual(newSettings, pick(currentLayerSettings, keys(newSettings)))) {
        appliedMissingLayerChanges.add(change);
      }
      if (missingLayerSettings[layerId]) {
        missingLayerSettings[layerId].value = {
          ...currentLayerSettings,
          ...newSettings,
        };
      } else {
        if (debug) {
          console.debug(`${changeFlow}: Creating new settings for layerId: ${layerId}`);
        }
        missingLayerSettings[layerId] = signal<LayerVisualizationSettings>({
          ...defaultLayerVisualizationSettings,
          ...newSettings,
        });
      }
    });
    const slideViewerSettings = isEmpty(missingLayerSettings)
      ? existingSlideSettings
      : { ...existingSlideSettings, ...missingLayerSettings };
    if (!viewerSettings[slideId] || !isEmpty(missingLayerSettings)) {
      if (debug && !viewerSettings[slideId]) {
        console.debug(`${changeFlow}: Creating new settings for slideId: ${slideId}`);
      }
      viewerSettingsSignal.value = {
        ...viewerSettings,
        [slideId]: slideViewerSettings,
      };
    }
    const relevantChanges = filter(changes, (change) => {
      const { layerId, newSettings } = change;
      if (missingLayerSettings[layerId]) {
        return appliedMissingLayerChanges.has(change);
      }
      const heatmapSettings = slideViewerSettings[layerId];
      const currentHeatmapSettings = heatmapSettings.value;

      const isRelevantChange = !isEqual(newSettings, pick(currentHeatmapSettings, keys(newSettings)));
      if (debug) {
        console.debug(`${changeFlow}: isRelevantChange: ${isRelevantChange}`, {
          layerId,
          newSettings,
          currentHeatmapSettings,
        });
      }
      return isRelevantChange;
    });

    if (isEmpty(relevantChanges)) {
      if (debug) {
        console.debug(`${changeFlow}: No relevant changes to apply`);
      }
      return;
    }

    // Get experiment results for pre-signing
    const selectionChanges = filter(relevantChanges, ({ newSettings }) => {
      return Boolean(newSettings.selected);
    });

    const experimentResultsToPreSign: number[] = uniq(
      compact(
        map(selectionChanges, ({ layerId }) => {
          const layerExperimentResult = getExperimentResultFromHeatmapId(layerId);
          return isNumber(layerExperimentResult) ? layerExperimentResult : undefined;
        })
      )
    );
    const currentHeatmapsRequiringPreSigning = experimentResultsRequiringPreSigning.value;

    if (debug) {
      console.debug(`${changeFlow}: selectionChanges`, {
        selectionChanges,
        relevantChanges,
        experimentResultsToPreSign,
        currentHeatmapsRequiringPreSigning,
      });
    }

    // If there are new heatmaps requiring pre-signing, we add them to the existing ones
    // (i.e. at least one of the experimentResultsToPreSign is not in currentHeatmapsRequiringPreSigning)
    if (!isEmpty(difference(experimentResultsToPreSign, currentHeatmapsRequiringPreSigning))) {
      experimentResultsRequiringPreSigning.value = uniq([
        ...currentHeatmapsRequiringPreSigning,
        ...experimentResultsToPreSign,
      ]);
    }

    forEach(relevantChanges, ({ layerId, newSettings }) => {
      if (missingLayerSettings[layerId]) {
        return;
      }
      const heatmapSettings = slideViewerSettings?.[layerId];
      if (!heatmapSettings) {
        console.error(`${changeFlow}: Failed to find heatmap settings for layerId: ${layerId}`, {
          slideId,
          viewerIndex,
          currentSettings: cloneDeep(slideViewerSettings || {}),
        });
        return;
      }
      const currentHeatmapSettings = heatmapSettings.value;

      heatmapSettings.value = {
        ...defaultLayerVisualizationSettings,
        ...currentHeatmapSettings,
        ...newSettings,
      };

      if (debug) {
        console.debug(`${changeFlow}: Applying changes to layer visualization settings`, {
          slideId,
          viewerIndex,
          layerId,
          newSettings,
          currentHeatmapSettings,
        });
      }
    });

    if (!skipUrlUpdate && stainTypeId) {
      startTransition(() => {
        const newUrlValue = cloneDeep(urlLayerSettingsPerStainAndViewer || {});

        forEach(relevantChanges, ({ layerId, layerUrlKey = layerId, newSettings }) => {
          const heatmapSettings = slidesLayerVisualizationSettings[viewerIndex]?.value?.[slideId]?.[layerId];
          if (!heatmapSettings) {
            console.error(`${changeFlow}: Failed to find heatmap settings for layerId: ${layerId}`, {
              slideId,
              viewerIndex,
              currentSettings: cloneDeep(slideViewerSettings || {}),
            });
            return;
          }
          const previousUrlLayerSettings = urlLayerSettingsPerStainAndViewer?.[layerUrlKey] || {};
          const previousUrlStainSettings = previousUrlLayerSettings?.[stainTypeId] || {};
          const currentHeatmapSettings = heatmapSettings.value;

          const newUrlLayerSettingsForViewer = {
            ...currentHeatmapSettings,
            ...(previousUrlStainSettings?.[viewerIndex] || {}),
            ...newSettings,
          };
          newUrlValue[layerUrlKey] = {
            ...previousUrlLayerSettings,
            [stainTypeId]: {
              ...previousUrlStainSettings,
              [viewerIndex]: newUrlLayerSettingsForViewer,
            },
          };
        });
        const encodedUrlValue = CompressedJsonParam.encode(newUrlValue);
        if (isString(encodedUrlValue)) {
          // The url is only used in initialization, so we can use statelessUpdateUrlParams
          statelessUpdateUrlParams({ [urlKeyForLayerSettingsPerStainAndDisplayName]: encodedUrlValue });
        }
      });
    } else if (!skipUrlUpdate && !stainTypeId) {
      console.warn(`${changeFlow}: stainTypeId is required for URL updates`, { slideId, viewerIndex, stainTypeId });
    } else if (skipUrlUpdate && stainTypeId) {
      console.warn(`${changeFlow}: stainTypeId is not necessary when skipping URL updates is not provided`, {
        slideId,
        viewerIndex,
        stainTypeId,
      });
    }
  };

  return applyChangesToSlideLayerVisualizationSettings;
};
